import { i18n, useI18n } from 'i18n-utils'
import { ApiStatus, InvestFinancialService } from 'network'
import { useBaseInfoStore } from './use-base-info'
import { InvestFinancialTab, InvestFinancialTabTypes } from './use-invest-financial'

export const useInvestFinancialDetail = defineStore('investFinancialDetail', () => {
  const productDetail = ref<InvestFinancialResponse.InvestFinancialProductDetail>()
  const subProductList = ref<InvestFinancialResponse.SubProductItem[]>([])
  const activeSubProduct = ref<InvestFinancialResponse.SubProductItem>()
  const baseInfoStore = useBaseInfoStore()

  const amount = ref('')
  const { t } = useI18n()
  const route = useRoute()
  const autoSubscriptionProductList = ref<InvestFinancialResponse.AutoSubscriptionProductItem[]>([])

  const loading = ref(false)

  const getProducts = async () => {
    const res = await InvestFinancialService.getProducts({
      productType: '',
    })
    if (res.status !== ApiStatus.Success) return []
    return res.data
  }

  const refreshProductDetail = async () => {
    if (!productDetail.value?.productId) return
    await getProductDetail(productDetail.value.productId, true)
  }

  const getProductDetail = async (productId: InvestFinancialResponse.ProductId, isRefresh = false) => {
    if (loading.value) return
    if (!isRefresh) {
      loading.value = true
    }
    const res = await InvestFinancialService.getProductDetail({ productId })

    if (res.status !== ApiStatus.Success) {
      loading.value = false
      return
    }
    productDetail.value = res.data

    const isSpecial = res.data.isSpecial === 1

    // 活期和定期
    const periodOrFixed = [1, 2]
    if (periodOrFixed.includes(productDetail.value?.productType ?? 0)) {
      const list = await getProducts()
      const filterProduct = list.filter((item) => periodOrFixed.includes(item.productType) && item.itemId === productDetail.value?.itemId)
      const periodProduct = filterProduct.find((item) => item.productType === InvestFinancialTabTypes[InvestFinancialTab.Period])

      subProductList.value = filterProduct.map((item) => item.subProductList).flat()
      subProductList.value = subProductList.value.filter((item) => (isSpecial ? item.isSpecial === 1 : item.isSpecial !== 1))
      // 排序，活期在最前面， productType为活期，其他的默认排序
      subProductList.value = subProductList.value.sort((a) => {
        if (a.productType === 2) return -1

        return 0
      })

      if (subProductList.value.length > 0) {
        const query = route.query
        const subProductId = query.subProductId as string
        if (subProductId) {
          activeSubProduct.value = subProductList.value.find((item) => item.subProductId === +subProductId)
        } else {
          if (isSpecial) {
            activeSubProduct.value = subProductList.value.find((item) => item.isSpecial === 1)
          } else {
            activeSubProduct.value = subProductList.value[0]
          }
        }
      }

      if (periodProduct) {
        const productInfoRes = await InvestFinancialService.getProductDetail({ productId: periodProduct.productId })
        if (productInfoRes.status === ApiStatus.Success) {
          productDetail.value.rateList = productInfoRes.data.rateList
        }
      }

      const autoRes = await InvestFinancialService.getAutoSubscriptionProducts()
      if (autoRes.status === ApiStatus.Success) {
        autoSubscriptionProductList.value = autoRes.data
      }
    }
    loading.value = false
  }

  /** 是否有固定锁仓期限 */
  const isPeriod = (product: InvestFinancialResponse.SubProductItem) => {
    return product.lockPeriod != null && product.lockPeriod !== 0
  }

  const lockedPeriodCardText = (product: InvestFinancialResponse.SubProductItem) => {
    let lockedPeriodText = ''
    if (isPeriod(product)) {
      lockedPeriodText = i18n.global.t('{days}天', { days: product.lockPeriod ?? '' })
    } else {
      lockedPeriodText = i18n.global.t('活期')
    }
    return lockedPeriodText
  }

  const assetName = computed(() => {
    if (!productDetail.value?.itemId) return ''
    return baseInfoStore.getCoinInfoByItemId(productDetail.value?.itemId)?.itemName.toUpperCase()
  })

  /**
   * 产品每日收益 = 输入数量*年化利率/365 ==>  本金 --- 计算总收益专用
   */
  const productDepositEarningsPerDayNum = computed(() => {
    if (!productDetail.value?.itemId) return '0'
    const numPrecision = baseInfoStore.getCoinInfoByItemId(productDetail.value?.itemId)?.itemDetail.numPrecision
    const result = activeSubProduct.value?.annualRate?.multiplyStr(amount.value).multiplyStr(activeSubProduct.value?.userProfitRate).divideStr('100')
    const tmp = result?.divideStr('100').divideStr('365').toFloatNumber() || 0
    return tmp.isGreaterThan(0) ? tmp.toStringAsFixed(numPrecision!) : '0'
  })

  /**
   * 理财券每日收益 = 理财券抵扣数量*年化利率/365 ==> 券 --- 计算总收益专用
   */
  const productCouponEarningsPerDayNum = computed(() => {
    if (!productDetail.value?.itemId) return '0'
    const numPrecision = baseInfoStore.getCoinInfoByItemId(productDetail.value?.itemId)?.itemDetail.numPrecision
    const selectGiftAmount = 0
    const result = activeSubProduct.value?.annualRate?.multiplyStr(selectGiftAmount).multiplyStr(activeSubProduct.value?.userProfitRate).divideStr('100')
    const tmp = result?.divideStr('100').divideStr('365').toFloatNumber() || 0
    return tmp.isGreaterThan(0) ? tmp.toStringAsFixed(numPrecision!) : '0'
  })

  /** 预估每日总收益 */
  const productTotalEarningsPerDay = computed(() => {
    if (!productDetail.value?.itemId) return '0'
    const numPrecision = baseInfoStore.getCoinInfoByItemId(productDetail.value?.itemId)?.itemDetail.numPrecision
    const result = productDepositEarningsPerDayNum.value?.addStr(productCouponEarningsPerDayNum.value).toFloatNumber() || 0
    return result.isGreaterThan(0) ? result.toStringAsFixed(numPrecision!) : '0'
  })

  /** 产品规则 */
  const productRules = computed(() => {
    const query = route.query
    let productType = +(query.productType as string)
    productType = productType || productDetail.value!.productType
    if (!productType) return []
    switch (productType) {
      case InvestFinancialTabTypes[InvestFinancialTab.Fixed]:
        return [
          {
            title: t('收益说明'),
            subTitle: t('定期为固定利率方式计息，计息开始时间为申购日次日，到期日本息自动发放到现货账户，派息账户为现货账户'),
          },
          {
            title: t('申购流程'),
            subTitle: t('申购→计息→派息→赎回'),
          },
          {
            title: t('自动申购与赎回'),
            subTitle: t(
              '选择自动申购后，您的定期产品将在到期日后进行自动续期，您可以随时取消自动申购；定期产品支持到期前提前赎回产品，但提前赎回产品，会损失部分或全部奖励，一旦确认将于72小时内将扣除利息后的余额返还至您的现货账户，大量赎回申请或赎回限额限制时，您将暂时无法进行赎回操作，待额度或流动性恢复后可再次申请赎回',
            ),
          },
        ]
      case InvestFinancialTabTypes[InvestFinancialTab.Period]:
        return [
          {
            title: t('收益说明'),
            subTitle: t(
              '当日利息收益=日末资产*日末年化利率/365，计息开始时间为申购日次日，计息日次日开始派息，每日16点（UTC+8）发放前一日活期利息，派息账户为现货账户，根据计息当日利率结算',
            ),
          },
          {
            title: t('申购流程'),
            subTitle: t('申购→计息→派息→赎回'),
          },
          {
            title: t('自动申购与赎回'),
            subTitle: t(
              '选择自动申购后，您的现货资产若满足最低申购额，将于每日0点（UTC+8）进行自动申购活期理财产品，您可以随时取消自动申购；活期赎回支持部分赎回和全部赎回，赎回申请成功后，活期产品资产将立即退回到个人钱包。赎回产品可能存在赎回上限且额度根据实际情况调整，大量赎回申请或赎回限额限制时，您将暂时无法进行赎回操作，待额度或流动性恢复后可再次申请赎回',
            ),
          },
        ]

      case InvestFinancialTabTypes[InvestFinancialTab.QuantitativeFinance]:
        return [
          {
            title: t('收益说明'),
            subTitle: t('量化理财为固定利率方式计息，募集期期间不计息，到期日本息自动发放到现货账户，派息账户为现货账户'),
          },
          {
            title: t('利润分配'),
            subTitle: t('项目运作人利润分润占比 {rate}%，项目到期结算时，该比例利息将被扣除', { rate: activeSubProduct.value?.traderProfitRate ?? '0' }),
          },
          {
            title: t('申购流程'),
            subTitle: t('募集→计息→产品到期派息+赎回'),
          },
          {
            title: t('赎回'),
            subTitle: t(
              '量化理财产品支持到期前提前赎回产品，但提前赎回产品，会损失部分或全部奖励，一旦确认将于72小时内将扣除利息后的余额返还至您的现货账户，大量赎回申请或赎回限额限制时，您将暂时无法进行赎回操作，待额度或流动性恢复后可再次申请赎回',
            ),
          },
        ]

      case InvestFinancialTabTypes[InvestFinancialTab.ChainEarn]:
        return [
          {
            title: t('收益说明'),
            subTitle: t(
              '链上赚币为固定利率方式计息，募集期间不计息，产品到期后自动发放本息，派息账户为现货账户；由于链上不可抗力因素，你申购的产品可能会发生终止、取消、提前赎回，这可能会影响您的本息计算',
            ),
          },
          {
            title: t('利润分配'),
            subTitle: t('项目运作人利润分润占比 {rate}%，项目到期结算时，该比例利息将被扣除', { rate: activeSubProduct.value?.traderProfitRate ?? '0' }),
          },
          {
            title: t('申购流程'),
            subTitle: t('募集→计息→产品到期派息+赎回'),
          },
          {
            title: t('赎回'),
            subTitle: t(
              '该产品不支持提前赎回，一般情况下产品到期后系统自动发放本息，如遇到链上异常事件，赎回期可能需等待1-30天不等（异常情况包括不限于链上拥堵、链上合约攻击、黑客事件等）；特殊情况下的赎回，可能会造成您的本金损失（包括不限于链上合约平台漏洞、链上项目方跑路、黑客事件等链上不可抗力事件）',
            ),
          },
        ]

      default:
        return []
    }
  })

  const getStatusText = () => {
    let statusText = '--'
    if (productDetail.value?.status != null) {
      switch (productDetail.value?.status) {
        case 0:
          statusText = t('立即申购')
          break
        case 1:
          statusText = activeSubProduct.value?.isReserved === 1 ? t('您已预约成功') : t('预热中，点击预约提醒')
          break
        case 2:
          statusText = t('立即申购')
          break
        case 3:
          statusText = t('立即申购')
          break
        case 4:
          statusText = t('募集结束')
          break
        case 5:
          statusText = t('计息中')
          break
        default:
          break
      }
    }

    if (![0, 1, 2, 3, 4, 5, 6, 7, 8].includes(productDetail.value?.status ?? 0)) {
      statusText = t('募集已结束')
    }

    return statusText
  }

  const canPurchase = () => {
    // status 0 随时可存, 1募集预热中，2为募集开始，3为募集中，4为募集结束，5为记息中，6为本息发放中，7为本息已到账. 8募集失败
    return [0, 2, 3].includes(productDetail.value?.status ?? 0)
  }

  const submitEnable = computed(() => {
    if (!amount.value || amount.value.isLessThanOrEqualTo(0)) return false
    // if (activeSubProduct.value && +amount.value < +activeSubProduct?.value.minAmount) return false
    return canPurchase()
  })

  return {
    productDetail,
    getProductDetail,
    subProductList,
    lockedPeriodCardText,
    activeSubProduct,
    assetName,
    loading,
    productTotalEarningsPerDay,
    productDepositEarningsPerDayNum,
    productCouponEarningsPerDayNum,
    productRules,
    amount,
    getStatusText,
    submitEnable,
    refreshProductDetail,
    autoSubscriptionProductList,
  }
})
